import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateAgentEvalSuiteInput,
  CreateAgentEvalTestCaseInput,
  GenerateAgentEvalCasesInput,
  AgentEvalRunResponse,
  UpdateAgentEvalSuiteInput,
  UpdateAgentEvalTestCaseInput,
} from '@erp/rpc';
import { orpc, rpcClient } from 'src/api/rpc/client';
import { createLaunchRequestTracker } from '../launchRequestIdentity';

type RunAgentEvalInput = Omit<
  Parameters<typeof rpcClient.core.agentEvals.run>[0],
  'requestId'
>;

const QUERY_KEY_BASE = 'agent-evals';
const POLL_MS = 4000;
// Faster cadence while a case is live so the operator's turns feel real-time.
const LIVE_POLL_MS = 2000;
// A triggered run is reserved as queued before the ai-service call. Keep polling
// through startup so that row advances to running and begins streaming cases.
const RUN_APPEAR_WINDOW_MS = 90_000;
// Set on a successful trigger; read by the list's refetchInterval (same module).
let lastRunTriggeredAt = 0;

function runStillExecuting(status: AgentEvalRunResponse['status']): boolean {
  return status === 'running' || status === 'queued';
}

export function useListAgentEvalSuites() {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'suites', 'list'],
    queryFn: () => rpcClient.core.agentEvals.listSuites({}),
  });
}

export function useListAgentEvalRuns(suiteId: string | null) {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'runs', 'list', suiteId],
    queryFn: () => {
      if (!suiteId) throw new Error('suiteId is required');
      return rpcClient.core.agentEvals.listRuns({ suiteId });
    },
    enabled: !!suiteId,
    // Stream progress: poll while any run is executing, and for a window after a
    // trigger so the freshly-started run's (async-created) row shows without a refresh.
    refetchInterval: (query) => {
      if (query.state.data?.some((r) => runStillExecuting(r.status))) {
        return POLL_MS;
      }
      if (Date.now() - lastRunTriggeredAt < RUN_APPEAR_WINDOW_MS)
        return POLL_MS;
      return false;
    },
  });
}

/** The offline↔online correlation read (AI-340) — per mock version, the offline eval pass-rate
 * beside the online production success + drift rate, so drift the offline evals missed is visible.
 * Org-scoped by session. */
export function useEvalCorrelation(site?: string) {
  const query = useQuery(
    orpc.core.agentEvals.getEvalCorrelation.queryOptions({
      input: site ? { site } : {},
    }),
  );
  return { ...query, rows: query.data ?? [] };
}

export function useGetAgentEvalRun(runId: string | null) {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'runs', 'get', runId],
    queryFn: () => {
      if (!runId) throw new Error('runId is required');
      return rpcClient.core.agentEvals.getRun({ runId });
    },
    enabled: !!runId,
    refetchInterval: (query) => {
      const run = query.state.data?.run;
      if (!run || !runStillExecuting(run.status)) return false;
      return run.liveState ? LIVE_POLL_MS : POLL_MS;
    },
  });
}

/** Cross-run alignment scorecard (Phase 2): per-(contract element, oracle source) held/violated/n-a
 * across the suite's runs. DB-only. */
export function useContractAlignment(
  suiteId: string | null,
  windowDays?: number,
) {
  return useQuery({
    queryKey: [
      QUERY_KEY_BASE,
      'contractAlignment',
      suiteId,
      windowDays ?? null,
    ],
    queryFn: () => {
      if (!suiteId) throw new Error('suiteId is required');
      return rpcClient.core.agentEvals.contractAlignment({
        suiteId,
        ...(windowDays != null ? { windowDays } : {}),
      });
    },
    enabled: !!suiteId,
  });
}

/** The AI narrative for the alignment scorecard (Phase 2). Gated by `enabled` so the (paid) LLM
 * call only fires when the user opens the summary, and never re-runs on refocus. */
export function useAlignmentSummary(
  suiteId: string | null,
  windowDays: number | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'alignmentSummary', suiteId, windowDays ?? null],
    queryFn: () => {
      if (!suiteId) throw new Error('suiteId is required');
      return rpcClient.core.agentEvals.summarizeAlignment({
        suiteId,
        ...(windowDays != null ? { windowDays } : {}),
      });
    },
    enabled: enabled && !!suiteId,
    staleTime: Infinity,
  });
}

export function useRunAgentEval() {
  const queryClient = useQueryClient();
  const [launch] = useState(createLaunchRequestTracker);
  return useMutation({
    mutationFn: (input: RunAgentEvalInput) =>
      launch(input, (requestId) =>
        rpcClient.core.agentEvals.run({ ...input, requestId }),
      ),
    // Open the poll window, then refetch suites + runs so the reserved queued row
    // and its transition to running appear without a refresh.
    onSuccess: () => {
      lastRunTriggeredAt = Date.now();
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE] });
    },
  });
}

export function useCancelAgentEvalRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) =>
      rpcClient.core.agentEvals.cancelRun({ runId }),
    // Refetch the run + lists so the status flips to "cancelled" and the live
    // panel drops immediately.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE] });
    },
  });
}

// ---- Authoring CRUD (Phase 2) ----

export function useListAgentEvalTestCases(suiteId: string | null) {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'test-cases', 'list', suiteId],
    queryFn: () => {
      if (!suiteId) throw new Error('suiteId is required');
      return rpcClient.core.agentEvals.listTestCases({ suiteId });
    },
    enabled: !!suiteId,
  });
}

export function useCreateAgentEvalSuite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAgentEvalSuiteInput) =>
      rpcClient.core.agentEvals.createSuite(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'suites'],
      });
    },
  });
}

export function useUpdateAgentEvalSuite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAgentEvalSuiteInput) =>
      rpcClient.core.agentEvals.updateSuite(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'suites'],
      });
    },
  });
}

export function useCreateAgentEvalTestCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAgentEvalTestCaseInput) =>
      rpcClient.core.agentEvals.createTestCase(input),
    onSuccess: (testCase) => {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'test-cases', 'list', testCase.suiteId],
      });
    },
  });
}

export function useUpdateAgentEvalTestCase(suiteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAgentEvalTestCaseInput) =>
      rpcClient.core.agentEvals.updateTestCase(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'test-cases', 'list', suiteId],
      });
    },
  });
}

export function useGenerateAgentEvalCases() {
  return useMutation({
    mutationFn: (input: GenerateAgentEvalCasesInput) =>
      rpcClient.core.agentEvals.generateCases(input),
  });
}

export function useDeleteAgentEvalTestCase(suiteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      rpcClient.core.agentEvals.deleteTestCase({ id }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'test-cases', 'list', suiteId],
      });
    },
  });
}
